<template>
  <div class="mobile-login-container">
    <!-- Background decorativo -->
    <div class="mobile-background">
      <div class="mobile-wave-top"></div>
      <div class="mobile-wave-bottom"></div>
    </div>

    <!-- Contenido principal -->
    <div class="mobile-content">
      <!-- Sección del logo -->
      <div class="mobile-logo-section">
        <div class="mobile-logo-container">
          <img
            :src="logoUrl"
            alt="Sispro Software"
            class="mobile-logo"
          />
        </div>
        <h1 class="mobile-welcome-text">Bienvenido</h1>
        <p class="mobile-subtitle">Sistema de Producción</p>
        <p
          v-if="clientName"
          class="mobile-client-name"
        >
          {{ clientName }}
        </p>
      </div>

      <!-- Sección del formulario -->
      <div class="mobile-form-section">
        <div class="mobile-form">
          <el-form
            ref="mobileFormRef"
            :model="localFormData"
            :rules="formRules"
            @submit.prevent="handleSubmit"
          >
            <el-form-item
              prop="email"
              class="mobile-form-item"
            >
              <el-input
                v-model="localFormData.email"
                :placeholder="emailPlaceholder"
                type="email"
                class="mobile-input"
                :prefix-icon="User"
                autocomplete="email"
                :disabled="loading"
                @keydown.enter="handleSubmit"
              />
            </el-form-item>

            <el-form-item
              prop="password"
              class="mobile-form-item"
            >
              <el-input
                v-model="localFormData.password"
                :placeholder="passwordPlaceholder"
                type="password"
                class="mobile-input"
                :prefix-icon="Lock"
                show-password
                autocomplete="current-password"
                :disabled="loading"
                @keydown.enter="handleSubmit"
              />
            </el-form-item>

            <el-form-item class="mobile-switch-item">
              <el-switch
                v-model="localFormData.mantenerSesion"
                active-text="Mantener sesión iniciada"
                :disabled="loading"
              />
            </el-form-item>

            <el-form-item class="mobile-button-item">
              <el-button
                type="primary"
                class="mobile-login-button"
                :loading="loading"
                :disabled="loading"
                @click="handleSubmit"
              >
                {{ buttonText }}
              </el-button>
            </el-form-item>
          </el-form>
        </div>
        <span
          v-if="version"
          class="mobile-version"
        >
          {{ version }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  watch,
} from 'vue';
import {
  User,
  Lock,
} from '@element-plus/icons-vue';

// Props
const props = defineProps({
  formData: {
    type: Object,
    required: true,
    default: () => ({
      email: '',
      password: '',
      mantenerSesion: false,
    }),
  },
  formRules: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  logoUrl: {
    type: String,
    default: '/img/logo/logo-sispro.svg',
  },
  emailPlaceholder: {
    type: String,
    default: 'Correo electrónico',
  },
  passwordPlaceholder: {
    type: String,
    default: 'Contraseña',
  },
  buttonText: {
    type: String,
    default: 'Iniciar Sesión',
  },
  // Identidad pública del cliente (misma que muestra el login desktop); vacío = no se muestra
  clientName: {
    type: String,
    default: '',
  },
  version: {
    type: String,
    default: '',
  },
});

// Emits
const emit = defineEmits(['submit', 'update:formData']);

// Refs
const mobileFormRef = ref();

// Local reactive form data
const localFormData = reactive({
  email: props.formData.email || '',
  password: props.formData.password || '',
  mantenerSesion: !!props.formData.mantenerSesion,
});

// Watch for changes in props and update local data
watch(
  () => props.formData,
  (newData) => {
    localFormData.email = newData.email || '';
    localFormData.password = newData.password || '';
    localFormData.mantenerSesion = !!newData.mantenerSesion;
  },
  { immediate: true, deep: true },
);

// Watch local form data and emit changes
watch(
  localFormData,
  (newData) => {
    emit('update:formData', { ...newData });
  },
  { deep: true },
);

// Methods
const handleSubmit = async () => {
  if (!mobileFormRef.value) return;

  try {
    await mobileFormRef.value.validate();
    emit('submit', localFormData);
  } catch (error) {
    console.error('Error de validación:', error);
  }
};

const clearForm = () => {
  localFormData.email = '';
  localFormData.password = '';
  localFormData.mantenerSesion = false;
  if (mobileFormRef.value) {
    mobileFormRef.value.clearValidate();
  }
};

// Expose ref and methods for parent component
defineExpose({
  formRef: mobileFormRef,
  clearForm,
});
</script>

<style scoped>
/* Estilos para Mobile - Branding Sispro Software */
.mobile-login-container {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  background: linear-gradient(145deg, #409eff 0%, #66b1ff 35%, #8cc5ff 100%);
}

.mobile-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.mobile-background::before {
  content: '';
  position: absolute;
  top: -20%;
  left: -20%;
  width: 140%;
  height: 140%;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
    repeat;
  animation: patternMove 20s linear infinite;
}

.mobile-wave-top {
  position: absolute;
  top: -10%;
  right: -15%;
  width: 250px;
  height: 250px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
  animation: float 8s ease-in-out infinite;
}

.mobile-wave-bottom {
  position: absolute;
  bottom: -15%;
  left: -20%;
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
  animation: float 10s ease-in-out infinite reverse;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) scale(1);
  }
  50% {
    transform: translateY(-15px) scale(1.05);
  }
}

@keyframes patternMove {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(60px, 60px);
  }
}

.mobile-content {
  position: relative;
  z-index: 2;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 max(24px, env(safe-area-inset-right)) 0 max(24px, env(safe-area-inset-left));
  box-sizing: border-box;
}

/* Los hijos flex no deben ensancharse por su contenido (evita que el card se corte a la derecha) */
.mobile-content > * {
  min-width: 0;
  max-width: 100%;
}

.mobile-logo-section {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: max(40px, env(safe-area-inset-top));
  padding-bottom: 20px;
  text-align: center;
}

.mobile-logo-container {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.15),
    0 8px 16px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.mobile-logo-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  animation: shimmer 3s infinite;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.mobile-logo {
  max-width: 180px;
  height: auto;
  filter: brightness(1.15) contrast(1.1) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
  position: relative;
  z-index: 1;
}

.mobile-welcome-text {
  color: #ffffff;
  font-size: 28px;
  font-weight: 800;
  margin: 0 0 8px 0;
  text-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.8px;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
}

.mobile-subtitle {
  color: rgba(255, 255, 255, 0.95);
  font-size: 17px;
  margin: 0 0 8px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  font-weight: 400;
  letter-spacing: 0.2px;
}

.mobile-subtitle::after {
  content: 'El software de la Industria Gráfica';
  display: block;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 4px;
  font-style: italic;
  letter-spacing: 0.5px;
}

.mobile-client-name {
  margin: 10px 0 0;
  max-width: 100%;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  overflow-wrap: anywhere;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
}

.mobile-version {
  align-self: center;
  margin-top: 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}

.mobile-form-section {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-bottom: max(20px, env(safe-area-inset-bottom));
  min-height: 0;
}

.mobile-form {
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 24px 20px;
  box-shadow:
    0 25px 50px rgba(0, 0, 0, 0.15),
    0 10px 20px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.4);
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.mobile-form-item {
  margin-bottom: 20px;
}

.mobile-form-item :deep(.el-form-item__content) {
  margin-left: 0 !important;
}

.mobile-input {
  border-radius: 16px;
}

.mobile-input :deep(.el-input__wrapper) {
  border-radius: 18px;
  padding: 14px 18px;
  box-shadow:
    0 3px 12px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  border: 1.5px solid #e8eaf6;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.9);
}

.mobile-input :deep(.el-input__wrapper:hover) {
  border-color: #66b1ff;
  box-shadow:
    0 6px 16px rgba(102, 177, 255, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
}

.mobile-input :deep(.el-input__wrapper.is-focus) {
  border-color: #409eff;
  box-shadow:
    0 0 0 3px rgba(64, 158, 255, 0.15),
    0 8px 20px rgba(64, 158, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}

.mobile-input :deep(.el-input__inner) {
  font-size: 16px;
  color: #409eff;
  height: auto;
  font-weight: 500;
  letter-spacing: 0.2px;
}

.mobile-input :deep(.el-input__prefix) {
  color: #8cc5ff;
}

.mobile-switch-item {
  margin-bottom: 4px;
}

.mobile-switch-item :deep(.el-switch__label) {
  color: #606266;
  font-size: 14px;
}

.mobile-button-item {
  margin-bottom: 0;
  margin-top: 12px;
}

.mobile-login-button {
  width: 100%;
  height: 54px;
  border-radius: 18px;
  font-size: 17px;
  font-weight: 700;
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 50%, #8cc5ff 100%);
  border: none;
  box-shadow:
    0 10px 20px rgba(64, 158, 255, 0.3),
    0 4px 8px rgba(64, 158, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.8px;
  text-transform: uppercase;
  position: relative;
  overflow: hidden;
}

.mobile-login-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.mobile-login-button:hover {
  transform: translateY(-3px);
  box-shadow:
    0 16px 32px rgba(64, 158, 255, 0.4),
    0 8px 16px rgba(64, 158, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  background: linear-gradient(135deg, #337ecc 0%, #529fff 50%, #7db8ff 100%);
}

.mobile-login-button:hover::before {
  left: 100%;
}

.mobile-login-button:active {
  transform: translateY(-1px);
  box-shadow:
    0 6px 12px rgba(64, 158, 255, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.mobile-login-button.is-loading {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 50%, #8cc5ff 100%);
}

.mobile-login-button:disabled {
  opacity: 0.7;
  transform: none;
  box-shadow: 0 4px 8px rgba(64, 158, 255, 0.2);
}

/* Ajustes específicos para dispositivos pequeños como iPhone SE */
@media (max-height: 700px) and (max-width: 400px) {
  .mobile-content {
    padding: 0 16px;
    justify-content: space-between;
  }

  .mobile-logo-section {
    padding-top: 30px;
    padding-bottom: 15px;
    flex-shrink: 0;
  }

  .mobile-welcome-text {
    font-size: 24px;
    margin-bottom: 6px;
  }

  .mobile-subtitle {
    font-size: 14px;
    margin-bottom: 4px;
  }

  .mobile-subtitle::after {
    font-size: 11px;
    margin-top: 2px;
  }

  .mobile-logo {
    max-width: 140px;
  }

  .mobile-logo-container {
    padding: 16px;
    margin-bottom: 15px;
    border-radius: 16px;
    width: 100%;
  }

  .mobile-form-section {
    padding-bottom: 15px;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
  }

  .mobile-form {
    padding: 20px 16px;
    border-radius: 20px;
    margin: 0;
  }

  .mobile-form-item {
    margin-bottom: 16px;
  }

  .mobile-login-button {
    height: 48px;
    font-size: 15px;
  }
}

/* Ajustes específicos para pantallas muy pequeñas */
@media (max-height: 650px) {
  .mobile-logo-section {
    padding-top: 20px;
    padding-bottom: 15px;
  }

  .mobile-welcome-text {
    font-size: 26px;
    margin-bottom: 6px;
  }

  .mobile-logo {
    max-width: 160px;
  }

  .mobile-logo-container {
    padding: 20px;
    margin-bottom: 16px;
    width: 100%;
  }

  .mobile-form {
    padding: 22px 18px;
  }
}

@media (max-height: 580px) {
  .mobile-content {
    min-height: 100vh;
    max-height: 100vh;
    overflow-y: auto;
    justify-content: space-between;
  }

  .mobile-logo-section {
    padding-top: 15px;
    padding-bottom: 10px;
    flex-shrink: 0;
  }

  .mobile-welcome-text {
    font-size: 22px;
    margin-bottom: 4px;
  }

  .mobile-subtitle {
    font-size: 13px;
  }

  .mobile-logo {
    max-width: 120px;
  }

  .mobile-logo-container {
    padding: 12px;
    margin-bottom: 12px;
    width: 100%;
  }

  .mobile-subtitle::after {
    font-size: 10px;
    margin-top: 1px;
  }

  .mobile-form-section {
    padding-bottom: 10px;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    align-items: center;
  }

  .mobile-form {
    padding: 16px 14px;
  }

  .mobile-form-item {
    margin-bottom: 12px;
  }
}

/* Ajustes para landscape en móviles */
@media (max-height: 500px) and (orientation: landscape) {
  .mobile-content {
    flex-direction: row;
    align-items: center;
    padding: 16px 24px;
    gap: 24px;
  }

  .mobile-logo-section {
    flex: 0.8;
    padding: 0;
  }

  .mobile-form-section {
    flex: 1.2;
    padding: 0;
  }

  .mobile-welcome-text {
    font-size: 22px;
    margin-bottom: 6px;
  }

  .mobile-subtitle {
    font-size: 14px;
  }

  .mobile-subtitle::after {
    font-size: 10px;
  }

  .mobile-logo {
    max-width: 140px;
  }

  .mobile-logo-container {
    padding: 16px;
    margin-bottom: 16px;
    width: 100%;
  }

  .mobile-form {
    padding: 24px 20px;
  }

  .mobile-login-button {
    height: 48px;
    font-size: 15px;
  }
}

/* Ajustes específicos para iPhone SE y dispositivos similares */
@media (max-height: 568px) and (max-width: 320px) {
  .mobile-content {
    padding: 0 12px;
    min-height: 100vh;
    max-height: 100vh;
    overflow-y: auto;
    justify-content: space-evenly;
  }

  .mobile-logo-section {
    padding-top: 10px;
    padding-bottom: 8px;
    flex: 0 0 auto;
  }

  .mobile-logo-container {
    padding: 8px;
    margin-bottom: 8px;
    border-radius: 12px;
    width: 100%;
  }

  .mobile-logo {
    max-width: 100px;
  }

  .mobile-welcome-text {
    font-size: 20px;
    margin-bottom: 3px;
  }

  .mobile-subtitle {
    font-size: 12px;
    margin-bottom: 2px;
  }

  .mobile-subtitle::after {
    font-size: 9px;
    margin-top: 1px;
  }

  .mobile-form-section {
    padding-bottom: 8px;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mobile-form {
    padding: 12px 10px;
    border-radius: 16px;
    width: 100%;
  }

  .mobile-form-item {
    margin-bottom: 10px;
  }

  .mobile-login-button {
    height: 44px;
    font-size: 14px;
    border-radius: 12px;
  }

  .mobile-input :deep(.el-input__wrapper) {
    padding: 10px 14px;
    border-radius: 12px;
  }
}

/* Ajustes para pantallas extra pequeñas */
@media (max-width: 350px) {
  .mobile-content {
    padding: 0 16px;
  }

  .mobile-welcome-text {
    font-size: 26px;
  }

  .mobile-form {
    padding: 20px 16px;
  }

  .mobile-logo {
    max-width: 160px;
  }
}

/* Mejoras para viewport muy pequeño */
@media (max-height: 500px) {
  .mobile-content {
    min-height: 100vh;
    max-height: 100vh;
    overflow-y: auto;
    justify-content: space-between;
    padding: 0 16px;
  }

  .mobile-logo-section {
    padding-top: 8px;
    padding-bottom: 5px;
    flex-shrink: 0;
  }

  .mobile-form-section {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    align-items: center;
    padding-bottom: 8px;
  }
}

/* Mejoras para accesibilidad en dispositivos táctiles */
@media (hover: none) and (pointer: coarse) {
  .mobile-input :deep(.el-input__wrapper) {
    min-height: 44px;
  }

  .mobile-login-button {
    min-height: 48px;
  }
}

/* Asegurar que el contenido no se corte en ningún dispositivo */
@media (max-height: 450px) {
  .mobile-login-container {
    min-height: 100vh;
    max-height: 100vh;
    overflow: hidden;
  }

  .mobile-content {
    min-height: 100vh;
    max-height: 100vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>
